import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { startOfMonth, endOfMonth, subMonths } from 'date-fns'
import { AssetsReportsState, FilterValues } from './assetsReports.types'

// Default filter values
const getDefaultFilters = (): FilterValues => ({
  startDate: startOfMonth(subMonths(new Date(), 1)),
  endDate: endOfMonth(subMonths(new Date(), 1)),
  surgeonName: null,
  procedure: null,
  location: null,
  companyName: null,
  assetType: null,
  disposition: null,
  status: 'Submitted',
  dateRange: 'lastMonth',
  sortModel: [{ field: 'date', sort: 'desc' }],
  filterModel: { items: [] },
  columnVisibilityModel: {},
})

export const useAssetsReportsStore = create<AssetsReportsState>()(
  devtools(
    persist(
      (set, get) => ({
        // State
        filters: getDefaultFilters(),
        surgeries: [],
        isLoading: false,

        // History for undo/redo
        history: [getDefaultFilters()],
        historyIndex: 0,

        // Actions
        setFilters: (newFilters: Partial<FilterValues>) => {
          const currentFilters = get().filters
          const updatedFilters = { ...currentFilters, ...newFilters }

          // Add to history
          const { history, historyIndex } = get()
          const newHistory = history.slice(0, historyIndex + 1)
          newHistory.push(updatedFilters)

          set({
            filters: updatedFilters,
            history: newHistory,
            historyIndex: newHistory.length - 1,
          })
        },

        setLoading: (isLoading) => {
          set({ isLoading })
        },

        // History navigation
        goBack: () => {
          const { history, historyIndex } = get()
          if (historyIndex > 0) {
            const newIndex = historyIndex - 1
            set({
              filters: history[newIndex],
              historyIndex: newIndex,
            })
          }
        },

        goForward: () => {
          const { history, historyIndex } = get()
          if (historyIndex < history.length - 1) {
            const newIndex = historyIndex + 1
            set({
              filters: history[newIndex],
              historyIndex: newIndex,
            })
          }
        },

        canGoBack: () => {
          const { historyIndex } = get()
          return historyIndex > 0
        },

        canGoForward: () => {
          const { history, historyIndex } = get()
          return historyIndex < history.length - 1
        },

        resetFilters: () => {
          const defaultFilters = getDefaultFilters()
          const { history } = get()
          const newHistory = [...history, defaultFilters]

          set({
            filters: defaultFilters,
            history: newHistory,
            historyIndex: newHistory.length - 1,
          })
        },

        // Computed values
        getActiveFiltersCount: () => {
          const { filters } = get()
          return Object.entries(filters).reduce(
            (count, [key, value]) => {
              if (
                key === 'startDate' ||
                key === 'endDate' ||
                key === 'dateRange'
              ) {
                return count
              }
              if (key === 'status') {
                return value !== 'Submitted' ? count + 1 : count
              }
              if (Array.isArray(value) && value.length > 0) {
                return count + 1
              }
              return count
            },
            filters.dateRange && filters.dateRange !== 'custom' ? 1 : 0
          )
        },
      }),
      {
        name: 'assets-reports-store',
        partialize: (state) => ({
          filters: state.filters,
          history: state.history,
          historyIndex: state.historyIndex,
        }),
        storage: {
          getItem: (name) => {
            const str = localStorage.getItem(name)
            if (!str) return null

            const parsed = JSON.parse(str)

            // Convert date strings back to Date objects
            if (parsed.state?.filters) {
              if (parsed.state.filters.startDate) {
                parsed.state.filters.startDate = new Date(
                  parsed.state.filters.startDate
                )
              }
              if (parsed.state.filters.endDate) {
                parsed.state.filters.endDate = new Date(
                  parsed.state.filters.endDate
                )
              }
            }

            // Convert date strings in history back to Date objects
            if (parsed.state?.history) {
              parsed.state.history = parsed.state.history.map(
                (historyItem: any) => ({
                  ...historyItem,
                  startDate: historyItem.startDate
                    ? new Date(historyItem.startDate)
                    : historyItem.startDate,
                  endDate: historyItem.endDate
                    ? new Date(historyItem.endDate)
                    : historyItem.endDate,
                })
              )
            }

            return parsed
          },
          setItem: (name, value) => {
            localStorage.setItem(name, JSON.stringify(value))
          },
          removeItem: (name) => {
            localStorage.removeItem(name)
          },
        },
      }
    ),
    {
      name: 'assets-reports-store',
    }
  )
)

// Action hooks for cleaner usage
export const useAssetsReportsActions = () => {
  const store = useAssetsReportsStore()
  return {
    setFilters: store.setFilters,
    setLoading: store.setLoading,
    goBack: store.goBack,
    goForward: store.goForward,
    resetFilters: store.resetFilters,
  }
}
